import React from 'react';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import IconButton from '@mui/material/IconButton';
import Chip from '@mui/material/Chip';
import TextField from '@mui/material/TextField';
import Button from '@mui/material/Button';
import Link from '@mui/material/Link';
import Collapse from '@mui/material/Collapse';
import ThumbUpAltOutlinedIcon from '@mui/icons-material/ThumbUpAltOutlined';
import ThumbDownAltOutlinedIcon from '@mui/icons-material/ThumbDownAltOutlined';
import ThumbUpAltIcon from '@mui/icons-material/ThumbUpAlt';
import ThumbDownAltIcon from '@mui/icons-material/ThumbDownAlt';
import useVoyagerStore from '../../stores/useVoyagerStore';
import type { LandingAgentTurn } from '../../stores/slices/landingAgentSlice';

const UP_TAGS = ['Accurate', 'Fast', 'Clear', 'Helpful'];
const DOWN_PARTS = ['Headline', 'Numbers', 'SQL', 'Chart', 'Follow-ups'];

/**
 * `onClose` (the answer row's thumbs down): the same "What went wrong?" form, opened on its own — no thumbs row and no
 * recorded line of its own (the row's thumb says it); Submit and Cancel close it.
 */
export default function FeedbackBar({ turn, onClose }: { turn: LandingAgentTurn; onClose?: () => void }) {
  const submit = useVoyagerStore((s) => s.submitTurnFeedback);
  const clear = useVoyagerStore((s) => s.clearTurnFeedback);

  const opened = !!onClose;
  const isPartial = turn.result?.mission_status === 'partial';

  const [stagedRating, setStagedRating] = React.useState<'up' | 'down' | null>(
    isPartial || opened ? 'down' : null,
  );
  const [tags, setTags] = React.useState<string[]>([]);
  const [parts, setParts] = React.useState<string[]>([]);
  const [comment, setComment] = React.useState('');

  const submitted = turn.feedback || null;

  const togglePanel = (r: 'up' | 'down') => {
    if (submitted) return;
    if (stagedRating === r) {
      setStagedRating(null);
      setTags([]);
      setParts([]);
      setComment('');
    } else {
      setStagedRating(r);
      setTags([]);
      setParts([]);
      setComment('');
    }
  };

  const toggleTag = (t: string) => {
    setTags((prev) => (prev.includes(t) ? prev.filter((x) => x !== t) : [...prev, t]));
  };
  const togglePart = (p: string) => {
    setParts((prev) => (prev.includes(p) ? prev.filter((x) => x !== p) : [...prev, p]));
  };

  const handleSubmit = async () => {
    if (!stagedRating) return;
    await submit(turn.id, {
      rating: stagedRating,
      tags,
      parts: stagedRating === 'down' ? parts : undefined,
      comment: comment.trim() || undefined,
      submittedAt: Date.now(),
    });
    setStagedRating(isPartial ? 'down' : null);
    setTags([]);
    setParts([]);
    setComment('');
    onClose?.();
  };

  if (opened) {
    return (
      <Box data-testid="feedback-form" sx={{ p: 1.75, border: 1, borderColor: 'var(--line)', borderRadius: '12px', bgcolor: 'var(--panel, var(--surface))', display: 'flex', flexDirection: 'column', gap: 1.25 }}>
        <Typography variant="subtitle2">{isPartial ? 'What was missing?' : 'What went wrong?'}</Typography>
        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.75 }}>
          {DOWN_PARTS.map((p) => (
            <Chip key={p} label={p} size="small" variant={parts.includes(p) ? 'filled' : 'outlined'}
              color={parts.includes(p) ? 'error' : 'default'} onClick={() => togglePart(p)} aria-pressed={parts.includes(p)} />
          ))}
        </Box>
        <TextField size="small" fullWidth multiline minRows={2} placeholder="Anything else? (optional)" value={comment}
          onChange={(e) => setComment(e.target.value)} inputProps={{ 'aria-label': 'Anything else about what went wrong' }}
          sx={{ '& .MuiInputBase-input': { fontSize: 13 }, '& .MuiInputBase-input::placeholder': { fontSize: 13 } }} />
        <Box sx={{ display: 'flex', gap: 1 }}>
          <Button size="small" variant="contained" onClick={handleSubmit}>Submit feedback</Button>
          <Button size="small" variant="text" onClick={onClose}>Cancel</Button>
        </Box>
      </Box>
    );
  }

  if (submitted) {
    return (
      <Box sx={{ mt: 2, pt: 1.5, borderTop: 1, borderColor: 'divider', display: 'flex', alignItems: 'center', gap: 1 }}>
        {submitted.rating === 'up'
          ? <ThumbUpAltIcon fontSize="small" sx={{ color: 'success.main' }} />
          : <ThumbDownAltIcon fontSize="small" sx={{ color: 'error.main' }} />}
        <Typography variant="caption" color="text.secondary" sx={{ flex: 1 }}>
          Thanks — feedback recorded
          {submitted.tags && submitted.tags.length > 0 ? ` · ${submitted.tags.join(', ')}` : ''}
        </Typography>
        <Link component="button" variant="caption" underline="hover" onClick={() => clear(turn.id)} sx={{ color: 'text.secondary' }}>
          Undo
        </Link>
      </Box>
    );
  }

  const prompt = isPartial
    ? 'What was missing?'
    : stagedRating === 'up'
      ? 'What worked?'
      : 'What went wrong?';

  return (
    <Box sx={{ mt: 2, pt: 1.5, borderTop: 1, borderColor: 'divider' }}>
      {!isPartial && (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
          <Typography variant="caption" color="text.secondary" sx={{ mr: 1 }}>
            Was this helpful?
          </Typography>
          <IconButton size="small" onClick={() => togglePanel('up')} sx={{ color: stagedRating === 'up' ? 'success.main' : 'text.secondary' }}>
            {stagedRating === 'up' ? <ThumbUpAltIcon fontSize="small" /> : <ThumbUpAltOutlinedIcon fontSize="small" />}
          </IconButton>
          <IconButton size="small" onClick={() => togglePanel('down')} sx={{ color: stagedRating === 'down' ? 'error.main' : 'text.secondary' }}>
            {stagedRating === 'down' ? <ThumbDownAltIcon fontSize="small" /> : <ThumbDownAltOutlinedIcon fontSize="small" />}
          </IconButton>
        </Box>
      )}

      <Collapse in={stagedRating !== null}>
        <Box sx={{ mt: isPartial ? 0 : 1.5, p: 1.5, bgcolor: 'background.default', borderRadius: 1.5, border: 1, borderColor: 'divider' }}>
          <Typography variant="subtitle2" sx={{ mb: 1 }}>{prompt}</Typography>

          {stagedRating === 'down' ? (
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.75, mb: 1.25 }}>
              {DOWN_PARTS.map((p) => (
                <Chip
                  key={p}
                  label={p}
                  size="small"
                  variant={parts.includes(p) ? 'filled' : 'outlined'}
                  color={parts.includes(p) ? 'error' : 'default'}
                  onClick={() => togglePart(p)}
                />
              ))}
            </Box>
          ) : (
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.75, mb: 1.25 }}>
              {UP_TAGS.map((t) => (
                <Chip
                  key={t}
                  label={t}
                  size="small"
                  variant={tags.includes(t) ? 'filled' : 'outlined'}
                  color={tags.includes(t) ? 'success' : 'default'}
                  onClick={() => toggleTag(t)}
                />
              ))}
            </Box>
          )}
          <TextField
            size="small"
            fullWidth
            multiline
            minRows={2}
            placeholder="Anything else? (optional)"
            value={comment}
            onChange={(e) => setComment(e.target.value)}
            sx={{ mb: 1.5, '& .MuiInputBase-input': { fontSize: 13 }, '& .MuiInputBase-input::placeholder': { fontSize: 13 } }}
          />
          <Box sx={{ display: 'flex', gap: 1 }}>
            <Button size="small" variant="contained" onClick={handleSubmit}>
              Submit feedback
            </Button>
            {!isPartial && (
              <Button size="small" variant="text" onClick={() => { setStagedRating(null); setTags([]); setParts([]); setComment(''); }}>
                Cancel
              </Button>
            )}
          </Box>
        </Box>
      </Collapse>
    </Box>
  );
}
